// ============================================================================
// CONTACT SEARCH FILTER COMPONENT
// Async searchable dropdown for filtering by contact
// Date: December 2025
// ============================================================================

'use client';

import React, { useState, useCallback } from 'react';
import AsyncSelect from 'react-select/async';
import { authService } from '@/lib/authService';
import { User, Building2, X } from 'lucide-react';

function debounceCallback<T extends (...args: any[]) => void>(callback: T, waitMs: number) {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      callback(...args);
    }, waitMs);
  };
}

// ============================================================================
// TYPES
// ============================================================================

export interface ContactOption {
  value: number;
  label: string;
  contact_type: string;
  company_name?: string;
  contact_person?: string;
  contact_number?: string;
  email?: string;
  phone?: string;
}

interface ContactSearchFilterProps {
  value: ContactOption | null;
  onChange: (contact: ContactOption | null) => void;
  placeholder?: string;
  className?: string;
  contactTypes?: string[]; // Filter by contact types: ['customer', 'supplier', etc.]
  disabled?: boolean;
  isClearable?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function ContactSearchFilter({
  value,
  onChange,
  placeholder = 'Search contacts...',
  className = '',
  contactTypes,
  disabled = false,
  isClearable = true,
  size = 'sm'
}: ContactSearchFilterProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [defaultContactOptions, setDefaultContactOptions] = useState<ContactOption[]>([]);

  // API call to search contacts
  const searchContacts = useCallback(async (inputValue: string): Promise<ContactOption[]> => {
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return [];

      const params = new URLSearchParams({
        limit: '50',
        is_active: 'true'
      });

      // Only add search param if there's actual input
      if (inputValue && inputValue.trim()) {
        params.set('search', inputValue.trim());
      }

      // Add contact types filter if specified
      if (contactTypes && contactTypes.length > 0) {
        params.append('contact_types', contactTypes.join(','));
      }

      const response = await fetch(`/api/v1/contacts?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) return [];

      const data = await response.json();
      if (!data.success) return [];

      return data.data.map((contact: any) => ({
        value: contact.id,
        label: contact.company_name || contact.contact_person || `Contact #${contact.id}`,
        contact_type: contact.contact_type,
        company_name: contact.company_name,
        contact_person: contact.contact_person,
        contact_number: contact.contact_number,
        email: contact.email,
        phone: contact.phone
      }));
    } catch (error) {
      console.error('Error searching contacts:', error);
      return [];
    }
  }, [contactTypes]);

  // Load default options on mount
  React.useEffect(() => {
    const loadDefaultOptions = async () => {
      setIsLoading(true);
      const options = await searchContacts('');
      setDefaultContactOptions(options);
      setIsLoading(false);
    };
    loadDefaultOptions();
  }, [contactTypes, searchContacts]);

  // Debounced load options for AsyncSelect
  const loadOptions = useCallback(
    (inputValue: string, callback: (options: ContactOption[]) => void) => {
      setIsLoading(true);
      searchContacts(inputValue)
        .then(options => {
          callback(options);
          setIsLoading(false);
        })
        .catch(() => {
          callback([]);
          setIsLoading(false);
        });
    },
    [searchContacts]
  );

  // Custom styles for react-select - matching dark mode and size
  const customStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: size === 'sm' ? '32px' : size === 'lg' ? '44px' : '38px',
      height: size === 'sm' ? '32px' : size === 'lg' ? '44px' : '38px',
      backgroundColor: 'var(--tw-bg-opacity, 1) rgb(255 255 255 / var(--tw-bg-opacity))',
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.2)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
      },
      fontSize: size === 'sm' ? '13px' : '14px',
      borderRadius: '6px',
      cursor: 'pointer'
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: size === 'sm' ? '0 8px' : '2px 12px'
    }),
    input: (base: any) => ({
      ...base,
      margin: 0,
      padding: 0
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: size === 'sm' ? '30px' : size === 'lg' ? '42px' : '36px'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: size === 'sm' ? '4px' : '8px',
      color: '#9ca3af',
      '&:hover': {
        color: '#6b7280'
      }
    }),
    clearIndicator: (base: any) => ({
      ...base,
      padding: size === 'sm' ? '4px' : '8px',
      color: '#9ca3af',
      '&:hover': {
        color: '#ef4444'
      }
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      border: '1px solid #e5e7eb',
      zIndex: 50
    }),
    menuList: (base: any) => ({
      ...base,
      padding: '4px'
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#3b82f6' 
        : state.isFocused 
          ? '#f3f4f6' 
          : 'white',
      color: state.isSelected ? 'white' : '#374151',
      padding: '8px 12px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: size === 'sm' ? '13px' : '14px',
      '&:active': {
        backgroundColor: state.isSelected ? '#3b82f6' : '#e5e7eb'
      }
    }),
    placeholder: (base: any) => ({
      ...base,
      color: '#9ca3af',
      fontSize: size === 'sm' ? '13px' : '14px'
    }),
    singleValue: (base: any) => ({
      ...base,
      color: '#374151',
      fontSize: size === 'sm' ? '13px' : '14px'
    }),
    noOptionsMessage: (base: any) => ({
      ...base,
      color: '#9ca3af',
      fontSize: size === 'sm' ? '13px' : '14px'
    }),
    loadingMessage: (base: any) => ({
      ...base,
      color: '#9ca3af',
      fontSize: size === 'sm' ? '13px' : '14px'
    })
  };

  // Dark mode styles
  const darkStyles = {
    ...customStyles,
    control: (base: any, state: any) => ({
      ...customStyles.control(base, state),
      backgroundColor: '#1f2937',
      borderColor: state.isFocused ? '#3b82f6' : '#374151',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#4b5563'
      }
    }),
    menu: (base: any) => ({
      ...customStyles.menu(base),
      backgroundColor: '#1f2937',
      border: '1px solid #374151'
    }),
    option: (base: any, state: any) => ({
      ...customStyles.option(base, state),
      backgroundColor: state.isSelected 
        ? '#3b82f6' 
        : state.isFocused 
          ? '#374151' 
          : '#1f2937',
      color: state.isSelected ? 'white' : '#e5e7eb'
    }),
    singleValue: (base: any) => ({
      ...customStyles.singleValue(base),
      color: '#e5e7eb'
    })
  };

  // Custom format for option labels
  const formatOptionLabel = (option: ContactOption) => {
    const isCustomer = option.contact_type === 'customer';
    const Icon = isCustomer ? Building2 : User;
    
    return (
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 flex-shrink-0 ${isCustomer ? 'text-blue-500' : 'text-purple-500'}`} />
        <div className="flex flex-col min-w-0">
          <span className="font-medium truncate">{option.label}</span>
          <div className="flex items-center gap-1.5">
            {option.contact_number && (
              <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                {option.contact_number}
              </span>
            )}
            {option.email && (
              <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {option.email}
              </span>
            )}
          </div>
        </div>
        <span className={`ml-auto text-xs px-1.5 py-0.5 rounded capitalize flex-shrink-0 ${
          isCustomer 
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' 
            : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
        }`}>
          {option.contact_type}
        </span>
      </div>
    );
  };

  // Check if dark mode
  const [isDark, setIsDark] = useState(false);
  React.useEffect(() => {
    const checkDark = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return (
    <div className={`${className}`}>
      <AsyncSelect
        value={value}
        onChange={onChange}
        loadOptions={loadOptions}
        defaultOptions={defaultContactOptions.length > 0 ? defaultContactOptions : true}
        cacheOptions
        isLoading={isLoading}
        placeholder={placeholder}
        isClearable={isClearable}
        isSearchable
        isDisabled={disabled}
        styles={isDark ? darkStyles : customStyles}
        formatOptionLabel={formatOptionLabel}
        noOptionsMessage={({ inputValue }) => 
          inputValue ? 'No contacts found' : (isLoading ? 'Loading contacts...' : 'No contacts found')
        }
        loadingMessage={() => 'Searching...'}
        classNamePrefix="contact-select"
      />
    </div>
  );
}
